import { apiCall, type ApiClientConfig } from "@playbookai/api-clients";
import type { PaginatedResponse, Solve } from "@playbookai/types";
import type { SolveCatalogItem } from "@playbookai/ui";

/**
 * The SOLVE picker behind the SOLVE Actions editor. `/v1/solves` already
 * returns only the SOLVEs the caller is a member of and pages by cursor, so
 * this is a projection, not a new access surface.
 */
export async function listSolveCatalogPage(
  config: ApiClientConfig,
  params: {
    readonly q?: string;
    readonly cursor?: string;
    readonly limit?: number;
    readonly signal?: AbortSignal;
  } = {},
): Promise<{
  readonly items: readonly SolveCatalogItem[];
  readonly nextCursor: string | null;
}> {
  const page = await apiCall<PaginatedResponse<Solve>>(
    config,
    "GET",
    "/v1/solves",
    undefined,
    { q: params.q, cursor: params.cursor, limit: params.limit ?? 20 },
    params.signal,
  );
  // Same guard as the VSM catalogue: a page that claims more without a cursor
  // would silently stop paging and read as "that is all of them".
  if (page.hasMore && !page.nextCursor) {
    throw new Error("SOLVE catalogue returned hasMore without a cursor.");
  }
  return {
    items: page.data.map((solve) => ({ id: solve.id, title: solve.title })),
    nextCursor: page.nextCursor,
  };
}

export async function getSolveCatalogItem(
  config: ApiClientConfig,
  solveId: string,
): Promise<SolveCatalogItem> {
  const solve = await apiCall<Solve>(
    config,
    "GET",
    `/v1/solves/${encodeURIComponent(solveId)}`,
  );
  return { id: solve.id, title: solve.title };
}
